/**
 * Live odds — Frontend-UI `MarketSseClient` + `RateSocketPayloadParser` port.
 *
 * Subscribe: Socket-API `GET /v1/stream?channels=…` (public, no key).
 * Market-API blasts `{marketId}` + `favouriteMarket_{id}` (`bookToSocketBlastItems`).
 * Bookmaker-API blasts Lab three-price batb/batl on `{id}` / `favouriteMarket_` / `market_`.
 * Lab / Session-API also blast `market_{id}` and `jodimarket_{id}`.
 *
 * Payload shapes (Frontend `rate_tick.dart`):
 *   rates[] / rates{selectionId} / rates.runners[] / batb|batl / ex.availableTo*
 *   fancy horseLay/horseBack + rateLay/rateBack
 *   bookmaker batb: [[worst,mid,best]] / batl: [[best,mid,worst]] (not [index,price,size])
 * Merge keeps catalog names and falls back to index order when ids diverge.
 */

import type {
  WorkstationBook,
  WorkstationOddLevel,
  WorkstationRunner,
} from '@/features/net-exposure/workstation-fixture'

/** Lab / Market-API ladder row: `[index, price, size]` (sometimes `[price, size]`). */
export type LiveRateTuple = Array<number | string> | { price?: number; size?: number; odds?: number }

export type LiveRateRunner = {
  id?: string | number
  betfairId?: string | number
  selectionId?: string | number
  name?: string
  runnerName?: string
  status?: string
  handicap?: number
  batb?: LiveRateTuple[]
  batl?: LiveRateTuple[]
  availableToBack?: Array<{ price?: number; size?: number }>
  availableToLay?: Array<{ price?: number; size?: number }>
  /** Raw listMarketBook runner (snapshot / alternate publishers). */
  ex?: {
    availableToBack?: Array<{ price?: number; size?: number }>
    availableToLay?: Array<{ price?: number; size?: number }>
  }
}

export type LiveRatePayload = {
  marketId?: string
  betfairId?: string
  /** Array, selection-keyed map, or `{ runners: [...] }` (Frontend parser). */
  rates?: LiveRateRunner[] | Record<string, unknown>
  runners?: LiveRateRunner[]
  runner?: LiveRateRunner[]
  betfairStatus?: string | null
  marketStatus?: string | null
  status?: string | null
  inplay?: boolean
  betDelay?: number | null
  sentTime?: string
  isAvailable?: boolean
  isSuspended?: boolean | string
  ballChalu?: boolean
  ballRunning?: boolean
  horseBack?: number | string
  horseLay?: number | string
  rateBack?: number | string
  rateLay?: number | string
  /** Bookmaker-API blasts — Lab three-price batb/batl, not exchange triples. */
  vendor?: string
  bookmakerMultiOdds?: boolean | string
  bookmakerCommission?: unknown
}

export type WorkstationLiveStatus = WorkstationBook['status']

function emptyLadder(): WorkstationOddLevel[] {
  return [
    { price: 0, size: 0 },
    { price: 0, size: 0 },
    { price: 0, size: 0 },
  ]
}

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Bookmaker-API / Lab BM: one inner array of three prices, no stake sizes
 * (`batb: [[worst, mid, best]]`, `batl: [[best, mid, worst]]`).
 * Depth 0 must be best — reverse back. Null when shape is exchange multi-row.
 * Frontend `OddsLevel.parseBookmakerLadder`.
 */
export function ladderFromBookmakerBat(
  bat: LiveRateTuple[] | undefined,
  back: boolean,
): WorkstationOddLevel[] | null {
  if (!bat || bat.length !== 1) return null
  const row = bat[0]
  if (!Array.isArray(row) || row.length === 0) return null
  const prices = (row as Array<number | string>).slice(0, 3).map(num)
  while (prices.length < 3) prices.push(0)
  const ordered = back ? [...prices].reverse() : prices
  const out = emptyLadder()
  ordered.forEach((price, i) => {
    if (i <= 2) out[i] = { price, size: 0 }
  })
  return out
}

/**
 * Bookmaker blasts carry `vendor: bookmaker`, a `#bookmaker` market id, or
 * bookmaker-only toggles — their ladders use the Lab three-price shape.
 * Frontend `_isBookmakerPayload`.
 */
export function isBookmakerPayload(
  payload: LiveRatePayload,
  roomOrMarketId?: string,
): boolean {
  if (String(payload.vendor ?? '')
    .toLowerCase()
    .includes('bookmaker')) {
    return true
  }
  if (payload.bookmakerMultiOdds != null || payload.bookmakerCommission != null) {
    return true
  }
  const ids = `${payload.marketId ?? ''} ${payload.betfairId ?? ''} ${roomOrMarketId ?? ''}`
  return ids.toLowerCase().includes('bookmaker')
}

/** Lab batb/batl → 3-deep back/lay ladder (index 0 = best). Frontend `_parseLadder`. */
export function ladderFromBat(bat: LiveRateTuple[] | undefined): WorkstationOddLevel[] {
  const out = emptyLadder()
  if (!bat?.length) return out
  let sequential = 0
  for (const row of bat) {
    let index = sequential
    let price = 0
    let size = 0
    if (Array.isArray(row) && row.length >= 2) {
      if (row.length >= 3) {
        index = num(row[0])
        price = num(row[1])
        size = num(row[2])
        if (index < 0 || index > 2) index = sequential
      } else {
        price = num(row[0])
        size = num(row[1])
      }
    } else if (isRecord(row)) {
      price = num(row.price ?? row.odds)
      size = num(row.size)
    } else if (typeof row === 'number' || typeof row === 'string') {
      price = num(row)
    } else {
      sequential += 1
      continue
    }
    if (index >= 0 && index <= 2) {
      out[index] = { price, size }
    }
    sequential += 1
  }
  return out
}

export function ladderFromOffers(
  offers: Array<{ price?: number; size?: number }> | undefined,
): WorkstationOddLevel[] {
  const out = emptyLadder()
  if (!offers?.length) return out
  offers.slice(0, 3).forEach((offer, i) => {
    out[i] = { price: num(offer.price), size: num(offer.size) }
  })
  return out
}

export function rateIds(rate: LiveRateRunner): string[] {
  return [rate.id, rate.selectionId, rate.betfairId, rate.name, rate.runnerName]
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => String(v).trim().toLowerCase())
}

function runnerMatchKey(value: string): string {
  return value.trim().toLowerCase()
}

function runnerMatchesRate(runner: WorkstationRunner, rate: LiveRateRunner): boolean {
  const ids = new Set(rateIds(rate))
  if (ids.size === 0) return false
  return ids.has(runnerMatchKey(runner.id)) || ids.has(runnerMatchKey(runner.name))
}

function laddersFromRate(
  rate: LiveRateRunner,
  bookmaker = false,
): {
  back: WorkstationOddLevel[]
  lay: WorkstationOddLevel[]
} {
  if (bookmaker) {
    const backBm = ladderFromBookmakerBat(rate.batb as LiveRateTuple[] | undefined, true)
    const layBm = ladderFromBookmakerBat(rate.batl as LiveRateTuple[] | undefined, false)
    if (backBm || layBm) {
      return {
        back: backBm ?? emptyLadder(),
        lay: layBm ?? emptyLadder(),
      }
    }
  }
  const backRaw = rate.batb ?? rate.availableToBack ?? rate.ex?.availableToBack
  const layRaw = rate.batl ?? rate.availableToLay ?? rate.ex?.availableToLay
  const backIsOffers = !rate.batb && (rate.availableToBack || rate.ex?.availableToBack)
  const layIsOffers = !rate.batl && (rate.availableToLay || rate.ex?.availableToLay)
  return {
    back: backIsOffers
      ? ladderFromOffers(backRaw as Array<{ price?: number; size?: number }> | undefined)
      : ladderFromBat(backRaw as LiveRateTuple[] | undefined),
    lay: layIsOffers
      ? ladderFromOffers(layRaw as Array<{ price?: number; size?: number }> | undefined)
      : ladderFromBat(layRaw as LiveRateTuple[] | undefined),
  }
}

function ladderHasPrice(levels: WorkstationOddLevel[]): boolean {
  return levels.some((l) => l.price > 0)
}

export function rateHasLadder(rate: LiveRateRunner, bookmaker = false): boolean {
  const { back, lay } = laddersFromRate(rate, bookmaker)
  return ladderHasPrice(back) || ladderHasPrice(lay)
}

/** Frontend `_runnersFromRates` — list, selection map, or nested `runners`. */
export function normalizeLiveRateRunners(payload: LiveRatePayload): LiveRateRunner[] {
  const rates = payload.rates ?? payload.runner
  if (Array.isArray(rates)) return rates.filter(isRecord) as LiveRateRunner[]
  if (isRecord(rates)) {
    if (Array.isArray(rates.runners)) {
      return (rates.runners as unknown[]).filter(isRecord) as LiveRateRunner[]
    }
    if (rates.batb != null || rates.batl != null) {
      return [rates as LiveRateRunner]
    }
    const out: LiveRateRunner[] = []
    for (const [key, value] of Object.entries(rates)) {
      if (key === 'runners' || !isRecord(value)) continue
      const row = value as LiveRateRunner
      out.push({ ...row, id: row.id ?? row.selectionId ?? row.betfairId ?? key })
    }
    return out
  }
  if (Array.isArray(payload.runners)) return payload.runners.filter(isRecord) as LiveRateRunner[]
  return []
}

function hasFancyHorse(payload: LiveRatePayload): boolean {
  // Session status blasts always include horseLay/horseBack (often 0). Treat
  // only positive run/rate values as a real fancy ladder so zeros never wipe
  // the last-known line after mergeLiveRatePayload.
  const fancy = fancyHorseFields(payload)
  return (
    num(fancy.horseBack) > 0 ||
    num(fancy.horseLay) > 0 ||
    num(fancy.rateBack) > 0 ||
    num(fancy.rateLay) > 0
  )
}

/** Prefer a positive run/rate; otherwise keep an explicit zero over `undefined`. */
function pickFancyValue(primary: unknown, secondary: unknown): unknown {
  if (num(primary) > 0) return primary
  if (num(secondary) > 0) return secondary
  if (primary !== undefined && primary !== null) return primary
  return secondary
}

/** Top-level Session fields, or nested Market-API `rates` map (Frontend FancyLinePrices). */
function fancyHorseFields(payload: LiveRatePayload): {
  horseLay: unknown
  horseBack: unknown
  rateLay: unknown
  rateBack: unknown
} {
  const nested = isRecord(payload.rates) ? payload.rates : null
  return {
    horseLay: pickFancyValue(payload.horseLay, nested?.horseLay),
    horseBack: pickFancyValue(payload.horseBack, nested?.horseBack),
    rateLay: pickFancyValue(payload.rateLay, nested?.rateLay),
    rateBack: pickFancyValue(payload.rateBack, nested?.rateBack),
  }
}

function isNoRunner(runner: WorkstationRunner): boolean {
  const id = runner.id.trim().toLowerCase()
  const name = runner.name.trim().toLowerCase()
  return id === 'no' || name === 'no'
}

function isYesRunner(runner: WorkstationRunner): boolean {
  const id = runner.id.trim().toLowerCase()
  const name = runner.name.trim().toLowerCase()
  return id === 'yes' || name === 'yes'
}

/** Empty fancy carrier so socket horseLay/horseBack have somewhere to land. */
export function fancyRateCarrier(marketName = 'Session'): WorkstationRunner {
  return {
    id: 'line',
    name: marketName || 'Session',
    amountPt: 0,
    amountTotal: 0,
    back: emptyLadder(),
    lay: emptyLadder(),
  }
}

function applyFancyHorseToRunners(
  runners: WorkstationRunner[],
  payload: LiveRatePayload,
): WorkstationRunner[] {
  const fancy = fancyHorseFields(payload)
  const noLevel = emptyLevel(fancy.horseLay, fancy.rateLay)
  const yesLevel = emptyLevel(fancy.horseBack, fancy.rateBack)
  const list = runners.length > 0 ? runners : [fancyRateCarrier()]
  const hasNoYes = list.some((r) => isNoRunner(r) || isYesRunner(r))

  return list.map((runner, index) => {
    if (hasNoYes) {
      if (isNoRunner(runner)) {
        const lay = [...runner.lay]
        if (noLevel.price > 0 || noLevel.size > 0) lay[0] = noLevel
        return { ...runner, lay }
      }
      if (isYesRunner(runner)) {
        const back = [...runner.back]
        if (yesLevel.price > 0 || yesLevel.size > 0) back[0] = yesLevel
        // Only Yes horse present: still land No on this carrier so FancyPanel
        // fallback `line?.lay[0]` can paint the lay cell.
        if (!list.some(isNoRunner) && (noLevel.price > 0 || noLevel.size > 0)) {
          const lay = [...runner.lay]
          lay[0] = noLevel
          return { ...runner, back, lay }
        }
        return { ...runner, back }
      }
      return runner
    }
    // Single session line: No=lay[0] (horseLay), Yes=back[0] (horseBack).
    if (index === 0) {
      const back = [...runner.back]
      const lay = [...runner.lay]
      if (yesLevel.price > 0 || yesLevel.size > 0) back[0] = yesLevel
      if (noLevel.price > 0 || noLevel.size > 0) lay[0] = noLevel
      return { ...runner, back, lay }
    }
    return runner
  })
}

/**
 * Frontend `mergeRunnersWithLiveTicks`: keep catalog names/P/L, swap ladders.
 * Id/name first, then index order when Market-API sends selection-only rows.
 * Fancy: Lab horseLay/horseBack → No|Yes boxes (not only index-0 when P/L horses exist).
 * Bookmaker: Lab three-price batb/batl (not exchange [index,price,size]).
 */
export function applyLiveRatesToRunners(
  runners: WorkstationRunner[],
  payload: LiveRatePayload,
  opts?: { bookmaker?: boolean },
): WorkstationRunner[] {
  const rates = normalizeLiveRateRunners(payload)
  if (hasFancyHorse(payload)) {
    return applyFancyHorseToRunners(runners, payload)
  }

  const bookmaker = opts?.bookmaker ?? isBookmakerPayload(payload)
  return runners.map((runner, index) => {
    const matched =
      rates.find((r) => runnerMatchesRate(runner, r) && rateHasLadder(r, bookmaker)) ??
      (rates.length === 1 && runners.length === 1 && rateHasLadder(rates[0]!, bookmaker)
        ? rates[0]
        : undefined) ??
      (index < rates.length && rateHasLadder(rates[index]!, bookmaker)
        ? rates[index]
        : undefined)

    if (!matched) return runner
    const { back, lay } = laddersFromRate(matched, bookmaker)
    return { ...runner, back, lay }
  })
}

function emptyLevel(price: unknown, size: unknown): WorkstationOddLevel {
  return { price: num(price), size: num(size) }
}

/** Lab workstation + Frontend `OddsSuspend.checkOuter`. */
export function statusFromLiveRates(payload: LiveRatePayload): WorkstationLiveStatus | null {
  if (payload.ballChalu || payload.ballRunning) return 'BALL_RUNNING'
  const raw = String(
    payload.betfairStatus ?? payload.marketStatus ?? payload.status ?? '',
  )
    .trim()
    .toUpperCase()
  if (payload.status === 'resultDeclared') return 'CLOSED'
  if (raw === 'CLOSED' || raw === 'SETTLED') return 'CLOSED'
  if (raw === 'OPEN' || raw === 'ACTIVE' || raw === 'ENABLE') return 'OPEN'
  if (raw === 'SAFE' || raw === 'BALL_RUNNING') return 'BALL_RUNNING'
  if (
    raw === 'YES' ||
    raw === 'SUSPENDED' ||
    raw === 'DISABLE' ||
    raw === 'DISABLED' ||
    raw === 'READY' ||
    raw === 'VOIDED'
  ) {
    return 'SUSPENDED'
  }
  if (payload.isSuspended === true || payload.isSuspended === 'Disable' || payload.isSuspended === 'Yes') {
    return 'SUSPENDED'
  }
  if (payload.isAvailable === false) return 'SUSPENDED'
  if (!raw && payload.isSuspended == null && payload.isAvailable == null) return null
  return null
}

export function applyLiveRatesToBook(
  book: WorkstationBook,
  payload: LiveRatePayload,
): WorkstationBook {
  const status = statusFromLiveRates(payload)
  const baseRunners =
    book.type === 'fancy' && book.runners.length === 0
      ? [fancyRateCarrier(book.marketName)]
      : book.runners
  const bookmaker = book.type === 'bookmaker' || isBookmakerPayload(payload, book.marketId)
  return {
    ...book,
    runners: applyLiveRatesToRunners(baseRunners, payload, { bookmaker }),
    status: status ?? book.status,
  }
}

/**
 * Seed FancyPanel No|Yes runs from open bets when SSE has not delivered a
 * horseLay/horseBack tick yet (common after opening Detail Book mid-session).
 * BACK/Yes → horseBack; LAY/No → horseLay. Rate size uses Lab-style 100 when
 * only decimal odds are on the bet row.
 *
 * Lab fancy lines are paired (default runButtonValue=1). When open bets only
 * cover one side — and Session often is not re-blasting static rates — fill the
 * missing No/Yes run so the workstation strip is not one-sided blank.
 */
export function fancyRatesFromBets(
  bets: Array<{
    type: string
    selectionName?: string
    runs?: number | null
    betRate?: number
    averagePriceMatched?: number
  }>,
): LiveRatePayload | null {
  let horseLay = 0
  let horseBack = 0
  let rateLay = 0
  let rateBack = 0
  for (const bet of bets) {
    const runs = bet.runs != null ? num(bet.runs) : 0
    if (!(runs > 0)) continue
    const side = String(bet.type || '').toUpperCase()
    const sel = String(bet.selectionName || '')
      .trim()
      .toLowerCase()
    const isNo = side === 'LAY' || sel === 'no'
    const isYes = side === 'BACK' || sel === 'yes'
    const rateRaw = num(bet.averagePriceMatched ?? bet.betRate)
    // Lab rateLay/rateBack are %-odds (~100); decimal odds (~2) → scale up.
    const rateSize = rateRaw >= 20 ? rateRaw : rateRaw > 1 ? rateRaw * 100 : 100
    if (isNo) {
      horseLay = runs
      rateLay = rateSize
    } else if (isYes) {
      horseBack = runs
      rateBack = rateSize
    }
  }
  if (!(horseLay > 0 || horseBack > 0)) return null
  // Default Lab runButtonValue=1: Yes runs N ⇒ No runs N-1 (and vice versa).
  if (horseBack > 1 && !(horseLay > 0)) horseLay = horseBack - 1
  if (horseLay > 0 && !(horseBack > 0)) horseBack = horseLay + 1
  if (rateBack > 0 && !(rateLay > 0)) rateLay = rateBack
  if (rateLay > 0 && !(rateBack > 0)) rateBack = rateLay
  return {
    horseLay: horseLay || undefined,
    horseBack: horseBack || undefined,
    rateLay: rateLay || undefined,
    rateBack: rateBack || undefined,
  }
}

/** True when `next` is older than `prev` (idempotent skip of out-of-order ticks). */
export function isStaleRateTick(
  prev: LiveRatePayload | null,
  next: LiveRatePayload,
): boolean {
  if (!prev?.sentTime || !next.sentTime) return false
  const a = Date.parse(prev.sentTime)
  const b = Date.parse(next.sentTime)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false
  return b < a
}

export function payloadBelongsToMarket(payload: LiveRatePayload, marketId: string): boolean {
  const id = marketId.trim()
  if (!id) return false
  const ids = [payload.marketId, payload.betfairId]
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => String(v))
  if (ids.length === 0) return true
  return ids.some((candidate) => candidate === id || candidate.endsWith(`_${id}`))
}

/**
 * Frontend `MarketSseClient.syncRooms` — already-qualified hubs stay as-is.
 * `marketEnable_*` must be checked before `market_*`.
 */
export function isSpecialSocketChannel(id: string): boolean {
  return (
    id === 'marketEnable' ||
    id === 'updateMatchStatus' ||
    id.startsWith('marketEnable_') ||
    id.startsWith('updateMatchStatus_') ||
    id.startsWith('favouriteMarket_') ||
    id.startsWith('jodimarket_') ||
    id.startsWith('AllSocket_Market_') ||
    id.startsWith('statusSocket_') ||
    id.startsWith('abandonSocket_') ||
    id.startsWith('resultDeclared_') ||
    id.startsWith('market_')
  )
}

/**
 * Parent exchange id for overlay markets:
 * - fancy custom `1.261942098-abc…` → `1.261942098`
 * - bookmaker `1.2#bookmaker1` → `1.2`
 * Session/fancy rates often fan out on the parent Match Odds channel.
 */
export function parentMarketId(marketId: string): string | null {
  const id = marketId.trim()
  if (!id) return null
  const hash = id.indexOf('#')
  if (hash > 0) return id.slice(0, hash)
  const dash = id.indexOf('-')
  if (dash > 0 && id.slice(0, dash).includes('.')) return id.slice(0, dash)
  return null
}

/**
 * Lab rooms + Market-API blast + Frontend aliases.
 * Subscribe to all so a publisher rename cannot stall the ladder.
 * Fancy/BM overlays also join the parent market room.
 */
export function channelsForMarket(marketId: string): string[] {
  const id = marketId.trim()
  if (!id) return []
  if (isSpecialSocketChannel(id)) return [id]
  const ids = [id]
  const parent = parentMarketId(id)
  if (parent) ids.push(parent)
  const out: string[] = []
  for (const mid of ids) {
    out.push(
      mid,
      `favouriteMarket_${mid}`,
      `market_${mid}`,
      `jodimarket_${mid}`,
      `AllSocket_Market_${mid}`,
    )
  }
  return [...new Set(out)]
}

/**
 * Socket subscribe may only use a market id from routing — never a free-form URL.
 * Lab ids look like `1.234`, fancy `1.234-uuid`, BM `1.234#bookmaker1`.
 */
export function isValidRatesMarketId(marketId: string): boolean {
  const id = marketId.trim()
  if (!id || id.length > 128) return false
  if (id.includes('://') || id.startsWith('//') || id.includes('..')) return false
  if (/[\/\\?\s]/.test(id)) return false
  return /^[A-Za-z0-9][A-Za-z0-9._#-]*$/.test(id)
}

export function marketRatesStreamPath(marketId: string): string {
  const channels = channelsForMarket(marketId)
  return `/v1/stream?channels=${encodeURIComponent(channels.join(','))}`
}

/**
 * Absolute SSE URL = config socket base + path for a validated market id only.
 * Returns null when base/id are missing or unsafe (caller must not fetch).
 */
export function marketRatesStreamUrl(socketBaseUrl: string, marketId: string): string | null {
  const id = marketId.trim()
  if (!isValidRatesMarketId(id)) return null
  const base = socketBaseUrl.trim()
  if (!base) return null
  try {
    const root = base.endsWith('/') ? base : `${base}/`
    return new URL(marketRatesStreamPath(id), root).href
  } catch {
    return null
  }
}

/** Frontend `_roomIdFromChannel` — strip Market-API / Lab prefixes. */
export function roomIdFromChannel(channel: string): string {
  const id = channel.trim()
  if (!id) return ''
  if (id === 'marketEnable' || id === 'updateMatchStatus') return id
  if (id.startsWith('marketEnable_') || id.startsWith('updateMatchStatus_')) return id
  if (id.startsWith('favouriteMarket_')) return id.slice('favouriteMarket_'.length)
  if (id.startsWith('jodimarket_')) return id.slice('jodimarket_'.length)
  if (id.startsWith('AllSocket_Market_')) return id.slice('AllSocket_Market_'.length)
  if (id.startsWith('market_')) return id.slice('market_'.length)
  return id
}

function isLiveRateShape(obj: Record<string, unknown>): boolean {
  if (Array.isArray(obj.rates) || Array.isArray(obj.runners) || Array.isArray(obj.runner)) return true
  if (isRecord(obj.rates)) return true
  if (obj.horseBack != null || obj.horseLay != null) return true
  if (obj.batb != null || obj.batl != null) return true
  if (typeof obj.marketId === 'string' && (obj.betfairStatus != null || obj.rates != null)) {
    return true
  }
  if (obj.status != null || obj.betfairStatus != null || obj.isSuspended != null || obj.ballChalu != null) {
    return true
  }
  return false
}

function pickFromFancyList(list: unknown[], marketId?: string): LiveRatePayload | null {
  const maps = list.filter(isRecord)
  if (maps.length === 0) return null
  const want = marketId?.trim() ?? ''
  if (want) {
    const hit = maps.find((row) => {
      const id = String(row.betfairId ?? row.marketId ?? '').trim()
      return id === want
    })
    if (hit) return hit as LiveRatePayload
    return null
  }
  if (maps.length === 1) return maps[0] as LiveRatePayload
  return null
}

/**
 * Socket-API SSE `data` is `{ channel, data: payload, at, id }`.
 * Lab Socket.IO emitted the payload directly.
 * Frontend also fans out Session-API fancy arrays on the match-odds channel.
 */
export function unwrapSocketFrame(raw: unknown, marketId?: string): LiveRatePayload | null {
  if (Array.isArray(raw)) return pickFromFancyList(raw, marketId)
  if (!isRecord(raw)) return null
  if (isLiveRateShape(raw)) return raw as LiveRatePayload
  if (Array.isArray(raw.data)) return pickFromFancyList(raw.data, marketId)
  if (raw.data != null) return unwrapSocketFrame(raw.data, marketId)
  return null
}

/**
 * One SSE frame → tick, or null to ignore (ping / other market / stale).
 * Mirrors Frontend `MarketSseClient._onEvent`.
 * Fancy overlays also accept the parent Match Odds room (Session-API array fan-out).
 */
export function liveOddsTickFromSse(
  eventName: string,
  dataJson: string,
  marketId: string,
  prev: LiveRatePayload | null,
): LiveRatePayload | null {
  if (eventName === 'ping' || eventName === 'error') return null
  if (eventName === 'connected') return null
  let parsed: unknown
  try {
    parsed = JSON.parse(dataJson) as unknown
  } catch {
    return null
  }
  const payload = unwrapSocketFrame(parsed, marketId)
  if (!payload) return null

  const channel = isRecord(parsed) ? String(parsed.channel ?? '').trim() : ''
  const room = roomIdFromChannel(channel)
  const parent = parentMarketId(marketId)
  const roomMatchesExact =
    Boolean(room) && (room === marketId || room.endsWith(`_${marketId}`))
  const roomMatchesParent =
    Boolean(room) &&
    parent != null &&
    (room === parent || room.endsWith(`_${parent}`))
  const onThisChannel = roomMatchesExact || roomMatchesParent
  if (!onThisChannel && !payloadBelongsToMarket(payload, marketId)) return null
  // Parent Match Odds ladders must not paint onto a fancy/BM overlay.
  if (roomMatchesParent && !roomMatchesExact) {
    const hasId =
      (payload.marketId != null && String(payload.marketId).trim() !== '') ||
      (payload.betfairId != null && String(payload.betfairId).trim() !== '')
    if (hasId && !payloadBelongsToMarket(payload, marketId)) return null
    if (!hasId && !hasFancyHorse(payload)) return null
  }
  if (isStaleRateTick(prev, payload)) return null
  return mergeLiveRatePayload(prev, payload)
}

/**
 * Session always emits horse and rate fields (often 0 on the unset side). Keep
 * the last positive value when the next tick zeros only one side — otherwise a
 * Yes-only update blanks No on the workstation strip (and vice versa).
 */
function keepFancyField(
  nextVal: number | string | undefined,
  prevVal: number | string | undefined,
): number | string | undefined {
  if (num(nextVal) > 0) return nextVal
  if (num(prevVal) > 0) return prevVal
  return nextVal !== undefined ? nextVal : prevVal
}

/**
 * Keep last-known ladders when a later tick is status-only, empty rates, or
 * one-sided fancy. Workstation rebuilds runners from books (empty ladder) on
 * every poll, so applying only the latest frame would blank prices after a
 * suspend tick.
 */
export function mergeLiveRatePayload(
  prev: LiveRatePayload | null,
  next: LiveRatePayload,
): LiveRatePayload {
  if (!prev) return next
  const bookmaker = isBookmakerPayload(next)
  const prevFancy = fancyHorseFields(prev)
  const nextFancy = fancyHorseFields(next)
  const nextHasLadder =
    normalizeLiveRateRunners(next).some((r) => rateHasLadder(r, bookmaker)) ||
    hasFancyHorse(next)
  // Lift preserved fancy runs to top-level so nested `rates` zeros cannot blank
  // No|Yes after a one-sided Session/Market-API tick.
  const fancyKept = {
    horseBack: keepFancyField(
      nextFancy.horseBack as number | string | undefined,
      prevFancy.horseBack as number | string | undefined,
    ),
    horseLay: keepFancyField(
      nextFancy.horseLay as number | string | undefined,
      prevFancy.horseLay as number | string | undefined,
    ),
    rateBack: keepFancyField(
      nextFancy.rateBack as number | string | undefined,
      prevFancy.rateBack as number | string | undefined,
    ),
    rateLay: keepFancyField(
      nextFancy.rateLay as number | string | undefined,
      prevFancy.rateLay as number | string | undefined,
    ),
  }
  if (nextHasLadder) {
    return {
      ...prev,
      ...next,
      ...fancyKept,
    }
  }

  return {
    ...prev,
    ...next,
    rates: prev.rates,
    runners: next.runners ?? prev.runners,
    runner: next.runner ?? prev.runner,
    horseBack: fancyKept.horseBack ?? prev.horseBack,
    horseLay: fancyKept.horseLay ?? prev.horseLay,
    rateBack: fancyKept.rateBack ?? prev.rateBack,
    rateLay: fancyKept.rateLay ?? prev.rateLay,
  }
}

export function oddsOverlayLabel(status: WorkstationLiveStatus): string | null {
  if (status === 'OPEN') return null
  if (status === 'CLOSED') return 'Closed'
  if (status === 'BALL_RUNNING') return 'Ball Running'
  return 'Suspended'
}
